import React from 'react'
import * as moment from 'moment-timezone'
import autobind from 'autobind-decorator'
import { BetterComponent } from '../better-component/better-component'
import { AppState } from '../../stores/app'
import { AppService, NotificationService } from '../../services'
import { Container } from 'typescript-ioc/es5'
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import { Input, Input as AqeInput } from '@pushly/aqe/lib/components'
import type { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { preventBubbling } from '../../_utils/utils'
import { axiosCancellationRequests } from '../../config/axios-setup'
import { SwPortal } from '../sw-portal/sw-portal'
import * as randomstring from 'randomstring'
import { BASE_TIME_FORMAT } from '../../constants'
import './notification-search-bar.scss'

type NotificationSource = 'MANUAL' | 'CAMPAIGN' | 'FEED'

interface INotificationSearchBarProps {
    value: string | undefined
    onChange?: (value: string | undefined) => void
    mode?: 'display' | 'search'
    sources?: NotificationSource[]
    autofocus?: boolean

    className?: string
    disabled?: boolean
    renderOption?: (notification: GetNotificationResponse) => React.ReactNode
    getDropdownContainer?: () => HTMLElement | any

    placeholder?: string
    reactive?: boolean
}

interface INotificationSearchBarState {
    expanded: boolean
    searching: boolean
    notifications: GetNotificationResponse[]
    tmpValue?: string
    searchQuery: string
}

export class NotificationSearchBar extends BetterComponent<INotificationSearchBarProps, INotificationSearchBarState> {
    private defaultClassName = 'sw-v2-notif-search-bar'
    private axiosCancellationKey = 'nsb.searchNotifications'

    private appState: AppState
    private appService: AppService
    private notifService: NotificationService

    private uid: string = randomstring.generate()
    private ref: any
    private inputRef: any
    private debounceTimer: number | undefined
    private debounceValue = 450

    public constructor(props: INotificationSearchBarProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.notifService = Container.get(NotificationService)

        this.state = {
            expanded: false,
            searching: false,
            notifications: [],
            searchQuery: '',
        }
    }

    public async componentDidMount(): Promise<void> {
        this.wireEventHandlers()

        if (this.hasValue) {
            return this.searchNotifications(this.props.value!, true)
        } else if (this.props.autofocus && !!this.inputRef && !!this.inputRef.inputRef) {
            this.inputRef.inputRef.focus()
        }
    }

    public async componentDidUpdate(prevProps: INotificationSearchBarProps): Promise<void> {
        if (this.isDisplayMode && this.hasValue) {
            if (!this.state.tmpValue || this.props.value !== this.state.tmpValue) {
                this.searchNotifications(this.props.value!, true)
            }
        }

        if (this.props.mode !== prevProps.mode && this.props.mode === 'search' && this.hasValue && !!this.selectedNotification) {
            await this.setState({
                searchQuery: `#${this.selectedNotification.id}: ${this.selectedNotification.template?.channels?.default?.title}`
            })

            if (!!this.inputRef && !!this.inputRef.input) {
                this.inputRef.input.focus()
                this.inputRef.input.select()
            }
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount()
        this.unwireEventHandlers()
    }

    public render(): React.ReactNode {
        return (
            <div ref={(el) => (this.ref = el)} className={this.buildRootClassNames()}>
                <div className={this.buildClassName('wrapper')}>
                    <span className={this.buildClassName('display')}>
                        {this.hasValue ? (
                            <>
                                <span className="option-id">#{this.props.value}:</span>
                                &nbsp;
                                <span className="option-title">
                                    {this.selectedNotification ? (
                                        this.selectedNotification.template?.channels?.default?.title
                                    ) : (
                                        <LoadingOutlined spin={true} />
                                    )}
                                </span>
                            </>
                        ) : (
                            '...'
                        )}
                    </span>

                        <div className={this.buildClassName('search')}>
                            <AqeInput
                                ref={(el) => (this.inputRef = el)}
                                className={this.buildClassName('search-input')}
                                reactive={this.props.reactive !== false}
                                value={this.state.searchQuery}
                                onChange={this.handleInputChange}
                                onFocus={(ev) => {
                                    ev.target.select()
                                    this.activate()
                                }}
                                placeholder={this.props.placeholder || 'Search by ID or Title'}
                                maxWidth={400}
                                icon={
                                    this.state.searching ? (
                                        <LoadingOutlined className="search-icon" spin={this.state.searching} />
                                    ) : (
                                        <SearchOutlined className="search-icon" spin={this.state.searching} />
                                    )
                                }
                            />
                        </div>

                    <SwPortal
                        className={this.buildRootClassNames('portal')}
                        portalId={this.buildClassName(`portal-${this.uid}`)}
                        container={this.portalContainer}
                        style={this.getPortalPositioning()}
                    >
                        <div className={this.buildRootClassNames('dropdown')}>
                            <div className={this.buildClassName('dropdown-wrapper')}>
                                <div
                                    className={`${this.buildClassName('dropdown-options')}${
                                        !this.hasOptions ? ' empty' : ''
                                    }`}
                                >
                                    {!this.hasOptions ? (
                                        <span>{this.isSearching ? 'Searching ...' : 'No notifications found'}</span>
                                    ) : (
                                        this.state.notifications.map((n) => this.renderOption(n))
                                    )}
                                </div>
                            </div>
                        </div>
                    </SwPortal>
                </div>
            </div>
        )
    }

    public renderOption(notification: GetNotificationResponse): React.ReactNode {
        const selected = this.hasValue && this.props.value === notification.id

        return (
            <div
                key={notification.id}
                className={`${this.buildClassName('dropdown-option')}${selected ? ' selected' : ''}`}
            >
                <div
                    className={this.buildClassName('dropdown-option-wrapper')}
                    onClick={() => this.handleValueChange(notification)}
                >
                    {!!this.props.renderOption ? (
                        this.props.renderOption(notification)
                    ) : (
                        <>
                            <div className={this.buildClassName('dropdown-option-upper')}>
                                <span className="option-id">#{notification.id}</span>
                                <span className="option-send-date">
                                    sent{' '}
                                    {moment
                                        .tz(notification.delivery_spec?.send_date_utc, this.globalDomain!.timezone)
                                        .format(BASE_TIME_FORMAT)}
                                </span>
                            </div>
                            <div className={this.buildClassName('dropdown-option-lower')}>
                                <span className="option-title">{notification.template?.channels?.default?.title}</span>
                            </div>
                            <div className={this.buildClassName('dropdown-option-lower')}>
                                <span className="option-body">{notification.template?.channels?.default?.body}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )
    }

    public getPortalPositioning(): any {
        if (this.ref) {
            const rect = this.ref.getBoundingClientRect()

            return [
                ['position', 'absolute'],
                ['top', `${Math.floor(rect.top) + Math.floor(rect.height)}px`],
                ['left', `${Math.floor(rect.left)}px`],
                ['width', `${Math.floor(rect.width)}px`],
            ]
                .map((p) => p.join(':'))
                .join(';')
        }
    }

    protected get isSearchMode(): boolean {
        return this.props.mode !== 'display'
    }

    protected get isDisplayMode(): boolean {
        return this.props.mode === 'display'
    }

    protected get isExpanded(): boolean {
        return this.state.expanded
    }

    protected get isSearching(): boolean {
        return this.state.searching
    }

    protected get hasOptions(): boolean {
        return this.state.notifications.length > 0
    }

    protected get hasValue(): boolean {
        return !!this.props.value
    }

    protected get hasInput(): boolean {
        return !!this.inputRef && !!this.inputRef.input.value.trim()
    }

    protected get inputValue(): string {
        return this.hasInput ? this.inputRef.input.value.trim() : ''
    }

    protected get selectedNotification(): GetNotificationResponse | undefined {
        let notif: GetNotificationResponse | undefined

        if (this.hasOptions && this.hasValue) {
            notif = this.state.notifications.find((n) => n.id === this.props.value)
        }

        return notif
    }

    protected get portalContainer(): HTMLElement {
        return !this.props.getDropdownContainer ? document.body : this.props.getDropdownContainer()
    }

    @autobind
    protected async handleValueChange(notif: GetNotificationResponse): Promise<void> {
        // clear loaded tmpValue
        await this.setState({
            tmpValue: undefined,
            searchQuery: `#${notif.id}: ${notif.template?.channels?.default?.title}`,
        })

        this.emitChangeEvent(notif.id)
        this.deactivate()
    }

    @autobind
    protected async handleInputChange(ev: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        const value = ev.target.value

        await this.setState({ searchQuery: value })

        this.emitChangeEvent(undefined)

        if (value.length >= 3) {
            this.searchNotifications(value)
        } else {
            this.deactivate()
        }
    }

    @autobind
    protected async activate(): Promise<void> {
        this.debounce(() => {
            if (this.hasInput && this.inputValue.length > 3) {
                this.searchNotifications(this.inputValue)
            }
        }, this.debounceValue / 2)
    }

    @autobind
    protected async deactivate(): Promise<void> {
        this.debounce(() => {
            this.cancelRequests()
            return this.setState(() => ({ expanded: false }))
        }, this.debounceValue / 2)
    }

    @autobind
    protected async cancelRequests(): Promise<void> {
        if (this.axiosCancellationKey in axiosCancellationRequests) {
            axiosCancellationRequests[this.axiosCancellationKey]()
        }

        return this.setState(() => ({
            searching: false,
            notifications: [],
        }))
    }

    protected async searchNotifications(query: string, reload: boolean = false): Promise<void> {
        this.setState(() => ({
            expanded: !reload,
            searching: true,
            tmpValue: reload ? query : undefined,
        }))

        const selectedPrettyString = this.selectedNotification
            ? `#${this.selectedNotification.id}: ${this.selectedNotification.template?.channels?.default?.title}`
            : ''

        // If the query matches exactly what we have selected, search by ID only
        let searchQuery = query
        if (query === selectedPrettyString && this.selectedNotification) {
            searchQuery = this.selectedNotification.id
        }

        this.debounce(async () => {
            const domainId = this.appState.currentDomain!.id
            const opts = {
                search: searchQuery,
                pagination: 0,
                source: (this.props.sources || ['MANUAL']).join(','),
                status: 'ACTIVE,COMPLETED,SCHEDULED,QUEUED,DELIVERING',
            }

            const cancellationKey = `${this.axiosCancellationKey}-${this.uid}`
            const { notifications, meta, cancelled } = await this.notifService.fetchNotificationsByDomainId(
                domainId,
                opts,
                false,
                cancellationKey,
            )

            await this.setState(({ expanded, notifications: currentNotifs }) => ({
                notifications: cancelled ? currentNotifs : notifications,
                searching: expanded && cancelled,
                searchQuery:
                    reload && !cancelled && !!this.selectedNotification
                        ? `#${this.selectedNotification.id}: ${this.selectedNotification.template?.channels?.default?.title}`
                        : this.state.searchQuery,
            }))
        })
    }

    protected debounce(fn: Function, timeout?: number) {
        if (this.debounceTimer != null) clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(fn, timeout ?? this.debounceValue)
    }

    protected emitChangeEvent(value: string | undefined): void {
        this.props.onChange?.(value)
    }

    protected buildClassName(className: string): string {
        return `${this.defaultClassName}-${className}`
    }

    protected buildRootClassNames(append?: string): string {
        const classNames: string[] = [
            !!append ? `${this.defaultClassName}-${append}` : this.defaultClassName,
            this.isDisplayMode ? 'mode-display' : 'mode-search',
            this.isExpanded ? 'expanded' : 'collapsed',
            this.isSearching ? 'searching' : 'static',
        ]
        if (this.props.className) classNames.push(this.props.className)

        return classNames.join(' ')
    }

    protected wireEventHandlers(): void {
        document.addEventListener('mousedown', this.handleDocumentClick.bind(this))
        this.ref.addEventListener('mousedown', preventBubbling)
    }

    protected unwireEventHandlers(): void {
        document.removeEventListener('mousedown', this.handleDocumentClick.bind(this))
        this.ref.removeEventListener('mousedown', preventBubbling)
    }

    protected handleDocumentClick(event: MouseEvent): void {
        if (this.isExpanded) this.deactivate()
    }
}
